import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  TextField,
  Table,
  Modal,
  TableBody,
  TableCell,
  Box,
  Typography,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  styled,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
  Close as CloseIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const ClientDetails = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalType, setSuccessModalType] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);

  const staticClientData = [
    { clientId: 1, clientCode: 'C001', clientName: 'John Doe', mobileNumber: '1234567890', email: 'john.doe@example.com', trnNo: 'TRN123456', country: 'USA', state: 'California', city: 'Los Angeles', poBox: '12345', address: '123 Main St', description: 'Sample Description 1' },
    { clientId: 2, clientCode: 'C002', clientName: 'Jane Smith', mobileNumber: '9876543210', email: 'jane.smith@example.com', trnNo: 'TRN987654', country: 'Canada', state: 'Ontario', city: 'Toronto', poBox: '67890', address: '456 Elm St', description: 'Sample Description 2' },
    { clientId: 3, clientCode: 'C003', clientName: 'Alice Johnson', mobileNumber: '5551234567', email: 'alice.j@example.com', trnNo: 'TRN555123', country: 'UK', state: 'England', city: 'London', poBox: '54321', address: '789 Oak St', description: 'Sample Description 3' },
    { clientId: 4, clientCode: 'C004', clientName: 'Bob Brown', mobileNumber: '4449876543', email: 'bob.brown@example.com', trnNo: 'TRN444987', country: 'Australia', state: 'New South Wales', city: 'Sydney', poBox: '09876', address: '321 Pine St', description: 'Sample Description 4' },
    { clientId: 5, clientCode: 'C005', clientName: 'Charlie Davis', mobileNumber: '7776665555', email: 'charlie.d@example.com', trnNo: 'TRN777666', country: 'Germany', state: 'Bavaria', city: 'Munich', poBox: '56789', address: '654 Cedar St', description: 'Sample Description 5' },
    { clientId: 6, clientCode: 'C006', clientName: 'Diana Evans', mobileNumber: '3332221111', email: 'diana.e@example.com', trnNo: 'TRN333222', country: 'USA', state: 'Texas', city: 'Houston', poBox: '11223', address: '987 Birch St', description: 'Sample Description 6' },
  ];

  const [clients, setClients] = useState(staticClientData);
  const [filteredData, setFilteredData] = useState(staticClientData);

  const handleAddClient = () => {
    setShowAddClientModal(true);
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setShowEditClientModal(true);
  };

  const handleDelete = (client) => {
    setSelectedClient(client);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedClient(null);
  };

  const handleConfirmDelete = () => {
    setClients((prevClients) => prevClients.filter((c) => c.clientId !== selectedClient.clientId));
    setSnackbarMessage(`${selectedClient.clientName} deleted successfully!`);
    setOpenSnackbar(true);
    setShowDeleteModal(false);
    setSelectedClient(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSaveClient = () => {
    setShowAddClientModal(false);
    setSuccessModalType('add');
    setShowSuccessModal(true);
  };

  const handleUpdateClient = () => {
    setShowEditClientModal(false);
    setSuccessModalType('edit');
    setShowSuccessModal(true);
  };

  useEffect(() => {
    setFilteredData(
      clients.filter(
        (client) =>
          client.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.clientCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.mobileNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.address.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, clients]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: {
              borderRadius: '10px',
            }
          }}
          sx={{ width: 300, maxWidth: '100%' }}
        />
        <Button
          variant="contained"
          onClick={handleAddClient}
          sx={{
            ml: 2,
            width: '160px',
            height: '50px',
            borderRadius: '10px',
            padding: '10px 0',
            fontWeight: 'bold',
            backgroundColor: theme.palette.primary.main,
            '&:hover': { backgroundColor: theme.palette.primary.dark },
          }}
          startIcon={<AddIcon />}
        >
          ADD CLIENT
        </Button>
      </Box>

    <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table
              sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Client Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Client Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Mobile Number</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Address</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((client, index) => (
                  <TableRow
                    key={client.clientId}
                    sx={{
                      backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <TableCell sx={{ fontSize: '1rem' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                    <TableCell sx={{ fontSize: '1rem' }}>{client.clientCode}</TableCell>
                    <TableCell sx={{ fontSize: '1rem' }}>{client.clientName}</TableCell>
                    <TableCell sx={{ fontSize: '1rem' }}>{client.mobileNumber}</TableCell>
                    <TableCell sx={{ fontSize: '1rem' }}>{client.email}</TableCell>
                    <TableCell sx={{ fontSize: '1rem' }}>{client.address}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleEdit(client.clientId)}
                        sx={{
                          color: theme.palette.primary.light,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(client.clientId, client.clientName)}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>Rows</InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Add Client Modal */}
      <AddClientDetailsModal
        open={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
        onSuccess={handleSaveClient}
      />

      {/* Edit Client Modal */}
      <EditClientDetailsModal
        open={showEditClientModal}
        onClose={() => setShowEditClientModal(false)}
        onSuccess={handleUpdateClient}
        client={selectedClient}
      />

      {/* Delete Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <CustomTableScrollbar
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedClient?.clientName}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleConfirmDelete}>
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </CustomTableScrollbar>
      </Modal>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              {successModalType === 'add' ? 'Client Added ✅' : 'Client Updated ✅'}
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              {successModalType === 'add' ? 'Client Added Successfully' : 'Client Updated Successfully'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => setShowSuccessModal(false)}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

// AddClientDetailsModal Component
const AddClientDetailsModal = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const [clientCode, setClientCode] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientNameArabic, setClientNameArabic] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [email, setEmail] = useState('');
  const [trnNo, setTrnNo] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [poBox, setPoBox] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');

  const staticCountryData = [
    { countryId: 1, countryName: 'USA' },
    { countryId: 2, countryName: 'Canada' },
    { countryId: 3, countryName: 'UK' },
    { countryId: 4, countryName: 'Australia' },
    { countryId: 5, countryName: 'Germany' },
  ];
  const staticStateData = {
    'USA': ['California', 'Texas', 'Florida', 'New York'],
    'Canada': ['Ontario', 'Quebec', 'British Columbia', 'Alberta'],
    'UK': ['England', 'Scotland', 'Wales', 'Northern Ireland'],
    'Australia': ['New South Wales', 'Victoria', 'Queensland', 'Western Australia'],
    'Germany': ['Bavaria', 'Hesse', 'Berlin', 'Hamburg'],
  };
  const staticCityData = {
    'USA': {
      'California': ['Los Angeles', 'San Francisco', 'San Diego'],
      'Texas': ['Houston', 'Dallas', 'Austin'],
      'Florida': ['Miami', 'Orlando', 'Tampa'],
      'New York': ['New York City', 'Buffalo', 'Rochester'],
    },
    'Canada': {
      'Ontario': ['Toronto', 'Ottawa', 'Mississauga'],
      'Quebec': ['Montreal', 'Quebec City', 'Laval'],
      'British Columbia': ['Vancouver', 'Victoria', 'Kelowna'],
      'Alberta': ['Calgary', 'Edmonton', 'Red Deer'],
    },
    'UK': {
      'England': ['London', 'Manchester', 'Birmingham'],
      'Scotland': ['Edinburgh', 'Glasgow', 'Aberdeen'],
      'Wales': ['Cardiff', 'Swansea', 'Newport'],
      'Northern Ireland': ['Belfast', 'Derry', 'Lisburn'],
    },
    'Australia': {
      'New South Wales': ['Sydney', 'Newcastle', 'Wollongong'],
      'Victoria': ['Melbourne', 'Geelong', 'Ballarat'],
      'Queensland': ['Brisbane', 'Gold Coast', 'Sunshine Coast'],
      'Western Australia': ['Perth', 'Mandurah', 'Bunbury'],
    },
    'Germany': {
      'Bavaria': ['Munich', 'Nuremberg', 'Augsburg'],
      'Hesse': ['Frankfurt', 'Wiesbaden', 'Darmstadt'],
      'Berlin': ['Berlin', 'Potsdam', 'Brandenburg'],
      'Hamburg': ['Hamburg', 'Altona', 'Harburg'],
    },
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (clientCode && clientName && mobileNo && email && trnNo && country && state && city && poBox && address && description) {
      onSuccess();
      handleCancel();
    } else {
      alert('Please fill all fields');
    }
  };

  const handleCancel = () => {
    setClientCode('');
    setClientName('');
    setClientNameArabic('');
    setMobileNo('');
    setEmail('');
    setTrnNo('');
    setCountry('');
    setState('');
    setCity('');
    setPoBox('');
    setAddress('');
    setDescription('');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
    >
      <CustomTableScrollbar
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 700,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
            Add Client Details
          </Typography>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ padding: '20px', overflowY: 'auto', flexGrow: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Client Code"
                variant="outlined"
                fullWidth
                value={clientCode}
                onChange={(e) => setClientCode(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="Client Name"
                variant="outlined"
                fullWidth
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Client Name (Arabic)"
                variant="outlined"
                fullWidth
                value={clientNameArabic}
                onChange={(e) => setClientNameArabic(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="Mobile No."
                variant="outlined"
                fullWidth
                value={mobileNo}
                onChange={(e) => setMobileNo(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="TRN No."
                variant="outlined"
                fullWidth
                value={trnNo}
                onChange={(e) => setTrnNo(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="country-label" sx={{ color: theme.palette.text.primary }}>Country</InputLabel>
                <Select
                  labelId="country-label"
                  id="country"
                  value={country}
                  label="Country"
                  onChange={(e) => { setCountry(e.target.value); setState(''); setCity(''); }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                  }}
                >
                  {staticCountryData.map((country) => (
                    <MenuItem key={country.countryId} value={country.countryName}>
                      {country.countryName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="state-label" sx={{ color: theme.palette.text.primary }}>State</InputLabel>
                <Select
                  labelId="state-label"
                  id="state"
                  value={state}
                  label="State"
                  onChange={(e) => { setState(e.target.value); setCity(''); }}
                  disabled={!country}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                  }}
                >
                  {country && staticStateData[country]?.map((stateItem) => (
                    <MenuItem key={stateItem} value={stateItem}>
                      {stateItem}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="city-label" sx={{ color: theme.palette.text.primary }}>City</InputLabel>
                <Select
                  labelId="city-label"
                  id="city"
                  value={city}
                  label="City"
                  onChange={(e) => setCity(e.target.value)}
                  disabled={!state}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                  }}
                >
                  {state && country && staticCityData[country]?.[state]?.map((cityItem) => (
                    <MenuItem key={cityItem} value={cityItem}>
                      {cityItem}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="PO Box"
                variant="outlined"
                fullWidth
                value={poBox}
                onChange={(e) => setPoBox(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Address"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="Description"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2 }}>
              <Tooltip
                title={
                  !clientCode ? "Enter Client Code" :
                  !clientName ? "Enter Client Name" :
                  !mobileNo ? "Enter Mobile No." :
                  !email ? "Enter Email" :
                  !trnNo ? "Enter TRN No." :
                  !country ? "Select Country" :
                  !state ? "Select State" :
                  !city ? "Select City" :
                  !poBox ? "Enter PO Box" :
                  !address ? "Enter Address" :
                  !description ? "Enter Description" : ""
                }
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                    }}
                    disabled={!clientCode || !clientName || !mobileNo || !email || !trnNo || !country || !state || !city || !poBox || !address || !description}
                  >
                    SAVE
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancel}
              >
                RESET
              </Button>
            </Box>
          </Box>
        </Box>
      </CustomTableScrollbar>
    </Modal>
  );
};

// EditClientDetailsModal Component
const EditClientDetailsModal = ({ open, onClose, onSuccess, client }) => {
  const theme = useTheme();
  const [clientCode, setClientCode] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientNameArabic, setClientNameArabic] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [email, setEmail] = useState('');
  const [trnNo, setTrnNo] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [poBox, setPoBox] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');

  const staticCountryData = [
    { countryId: 1, countryName: 'USA' },
    { countryId: 2, countryName: 'Canada' },
    { countryId: 3, countryName: 'UK' },
    { countryId: 4, countryName: 'Australia' },
    { countryId: 5, countryName: 'Germany' },
  ];
  const staticStateData = {
    'USA': ['California', 'Texas', 'Florida', 'New York'],
    'Canada': ['Ontario', 'Quebec', 'British Columbia', 'Alberta'],
    'UK': ['England', 'Scotland', 'Wales', 'Northern Ireland'],
    'Australia': ['New South Wales', 'Victoria', 'Queensland', 'Western Australia'],
    'Germany': ['Bavaria', 'Hesse', 'Berlin', 'Hamburg'],
  };
  const staticCityData = {
    'USA': {
      'California': ['Los Angeles', 'San Francisco', 'San Diego'],
      'Texas': ['Houston', 'Dallas', 'Austin'],
      'Florida': ['Miami', 'Orlando', 'Tampa'],
      'New York': ['New York City', 'Buffalo', 'Rochester'],
    },
    'Canada': {
      'Ontario': ['Toronto', 'Ottawa', 'Mississauga'],
      'Quebec': ['Montreal', 'Quebec City', 'Laval'],
      'British Columbia': ['Vancouver', 'Victoria', 'Kelowna'],
      'Alberta': ['Calgary', 'Edmonton', 'Red Deer'],
    },
    'UK': {
      'England': ['London', 'Manchester', 'Birmingham'],
      'Scotland': ['Edinburgh', 'Glasgow', 'Aberdeen'],
      'Wales': ['Cardiff', 'Swansea', 'Newport'],
      'Northern Ireland': ['Belfast', 'Derry', 'Lisburn'],
    },
    'Australia': {
      'New South Wales': ['Sydney', 'Newcastle', 'Wollongong'],
      'Victoria': ['Melbourne', 'Geelong', 'Ballarat'],
      'Queensland': ['Brisbane', 'Gold Coast', 'Sunshine Coast'],
      'Western Australia': ['Perth', 'Mandurah', 'Bunbury'],
    },
    'Germany': {
      'Bavaria': ['Munich', 'Nuremberg', 'Augsburg'],
      'Hesse': ['Frankfurt', 'Wiesbaden', 'Darmstadt'],
      'Berlin': ['Berlin', 'Potsdam', 'Brandenburg'],
      'Hamburg': ['Hamburg', 'Altona', 'Harburg'],
    },
  };

  useEffect(() => {
    if (client) {
      setClientCode(client.clientCode);
      setClientName(client.clientName);
      setMobileNo(client.mobileNumber);
      setEmail(client.email);
      setTrnNo(client.trnNo);
      setCountry(client.country);
      setState(client.state);
      setCity(client.city);
      setPoBox(client.poBox);
      setAddress(client.address);
      setDescription(client.description);
    }
  }, [client]);

  const handleSave = (e) => {
    e.preventDefault();
    if (clientCode && clientName && mobileNo && email && trnNo && country && state && city && poBox && address && description) {
      onSuccess();
      onClose();
    } else {
      alert('Please fill all fields');
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
    >
      <CustomTableScrollbar
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 700,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
            Edit Client Details
          </Typography>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ padding: '20px', overflowY: 'auto', flexGrow: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Client Code"
                variant="outlined"
                fullWidth
                value={clientCode}
                onChange={(e) => setClientCode(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="Client Name"
                variant="outlined"
                fullWidth
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Client Name (Arabic)"
                variant="outlined"
                fullWidth
                value={clientNameArabic}
                onChange={(e) => setClientNameArabic(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="Mobile No."
                variant="outlined"
                fullWidth
                value={mobileNo}
                onChange={(e) => setMobileNo(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="TRN No."
                variant="outlined"
                fullWidth
                value={trnNo}
                onChange={(e) => setTrnNo(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="country-label" sx={{ color: theme.palette.text.primary }}>Country</InputLabel>
                <Select
                  labelId="country-label"
                  id="country"
                  value={country}
                  label="Country"
                  onChange={(e) => { setCountry(e.target.value); setState(''); setCity(''); }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                  }}
                >
                  {staticCountryData.map((country) => (
                    <MenuItem key={country.countryId} value={country.countryName}>
                      {country.countryName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="state-label" sx={{ color: theme.palette.text.primary }}>State</InputLabel>
                <Select
                  labelId="state-label"
                  id="state"
                  value={state}
                  label="State"
                  onChange={(e) => { setState(e.target.value); setCity(''); }}
                  disabled={!country}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                  }}
                >
                  {country && staticStateData[country]?.map((stateItem) => (
                    <MenuItem key={stateItem} value={stateItem}>
                      {stateItem}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="city-label" sx={{ color: theme.palette.text.primary }}>City</InputLabel>
                <Select
                  labelId="city-label"
                  id="city"
                  value={city}
                  label="City"
                  onChange={(e) => setCity(e.target.value)}
                  disabled={!state}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                  }}
                >
                  {state && country && staticCityData[country]?.[state]?.map((cityItem) => (
                    <MenuItem key={cityItem} value={cityItem}>
                      {cityItem}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="PO Box"
                variant="outlined"
                fullWidth
                value={poBox}
                onChange={(e) => setPoBox(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Address"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="Description"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                }}
              >
                UPDATE
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancel}
              >
                CANCEL
              </Button>
            </Box>
          </Box>
        </Box>
      </CustomTableScrollbar>
    </Modal>
  );
};

export default ClientDetails;
